import React, { useState, useEffect } from 'react';
import { updateOpportunity, addPropertyFromOpportunity, markOpportunityViewed } from '../../services/opportunityService';
import { ExternalLink, Plus, Check, ChevronDown, ChevronUp, X, ImageIcon, MapPin } from '../../icons';
import Button from '../ui/Button';
import Sheet from '../ui/Sheet';
import PlatformBadge from '../ui/PlatformBadge';
import { cn } from '../ui/cn';

/** Teal ring showing the AI investment score out of 10. */
const ScoreRing = ({ score = 0, size = 52 }) => {
  const r = (size - 6) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(10, score)) / 10;
  const color = score >= 9 ? '#2EF2C6' : score >= 7 ? '#14D3B0' : score >= 5 ? '#FFB020' : '#F0525F';
  return (
    <div className="relative flex-shrink-0" style={{ width: size, height: size }} aria-label={`Score ${score} out of 10`}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} stroke="#2C3A52" strokeWidth="4" fill="#1E2839" />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke={color}
          strokeWidth="4"
          fill="none"
          strokeLinecap="round"
          strokeDasharray={`${c * pct} ${c}`}
        />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-[15px] font-bold tabular text-primary">
        {Number.isFinite(score) ? (Number.isInteger(score) ? score : score.toFixed(1)) : '–'}
      </span>
    </div>
  );
};

export default function OpportunityCard({ opportunity, userId }) {
  const [expanded, setExpanded] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showDismissConfirm, setShowDismissConfirm] = useState(false);
  const [actionMessage, setActionMessage] = useState(null);
  const [imgFailed, setImgFailed] = useState(false);
  
  const { aiAnalysis, comparison, marketData } = opportunity || {};

  // Auto-dismiss action messages after 3 seconds
  useEffect(() => {
    if (actionMessage) {
      const timer = setTimeout(() => setActionMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [actionMessage]);
  
  // Mark as viewed when expanded
  const handleExpand = async () => {
    if (!expanded && !opportunity.viewed) {
      try {
        await markOpportunityViewed(userId, opportunity.id);
      } catch (error) {
        console.error('Error marking as viewed:', error);
      }
    }
    setExpanded(!expanded);
  };
  
  const handleDismiss = async () => {
    setShowDismissConfirm(false);
    setIsProcessing(true);
    try {
      await updateOpportunity(userId, opportunity.id, {
        status: 'dismissed',
        dismissedAt: new Date(),
        dismissedReason: 'user-action'
      });
    } catch (error) {
      console.error('Error dismissing opportunity:', error);
      setActionMessage({ type: 'error', text: 'Failed to dismiss opportunity. Please try again.' });
    } finally {
      setIsProcessing(false);
    }
  };
  
  const handleAddToTracked = async () => {
    setIsProcessing(true);
    try {
      await addPropertyFromOpportunity(userId, opportunity);
      await updateOpportunity(userId, opportunity.id, {
        status: 'tracked',
        addedToTracked: true,
        addedToTrackedAt: new Date()
      });
      setActionMessage({ type: 'success', text: 'Property added to your tracked properties!' });
    } catch (error) {
      console.error('Error adding to tracked:', error);
      setActionMessage({ type: 'error', text: 'Failed to add property. Please try again.' });
    } finally {
      setIsProcessing(false);
    }
  };

  const fmt = (value, decimals = 0) =>
    value != null ? value.toFixed(decimals) : 'N/A';

  const fmtCurrency = (value) =>
    value != null ? `€${value.toLocaleString()}` : 'N/A';
  
  const getScoreBadge = (score) => {
    if (score >= 9) return 'Exceptional';
    if (score >= 7) return 'Strong buy';
    if (score >= 5) return 'Fair deal';
    return 'Overpriced';
  };

  const score = aiAnalysis?.investmentScore || 0;
  const discount = comparison?.bestDiscount;
  const stats = [
    opportunity.bedrooms && `${opportunity.bedrooms} bd`,
    opportunity.bathrooms && `${opportunity.bathrooms} ba`,
    opportunity.area != null && `${opportunity.area} m²`,
    comparison?.pricePerSqm != null && `€${fmt(comparison.pricePerSqm)}/m²`,
  ].filter(Boolean);
  const showImage = opportunity.imageUrl && !imgFailed;
  const isTracked = opportunity.status === 'tracked';
  const isDismissed = opportunity.status === 'dismissed';
  
  return (
    <article className={cn('bg-card border border-line rounded-card shadow-card overflow-hidden flex flex-col', isDismissed && 'opacity-75')}>
      {/* Media */}
      <div className="relative aspect-[16/9] bg-card-2">
        {showImage ? (
          <img src={opportunity.imageUrl} alt={opportunity.title} loading="lazy" referrerPolicy="no-referrer" className="w-full h-full object-cover" onError={() => setImgFailed(true)} />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#213043] to-[#152131] flex items-center justify-center text-muted">
            <ImageIcon className="w-10 h-10" />
          </div>
        )}
        <div className="absolute top-2.5 left-2.5 flex items-center gap-1.5">
          <PlatformBadge platform={opportunity.platform || opportunity.source} />
          {!opportunity.viewed && <span className="px-1.5 py-0.5 rounded-md bg-accent text-[#062A24] text-[11px] font-bold">NEW</span>}
          {isTracked && <span className="px-1.5 py-0.5 rounded-md bg-info text-white text-[11px] font-semibold">Tracked</span>}
          {isDismissed && <span className="px-1.5 py-0.5 rounded-md bg-card-2 text-secondary text-[11px] font-semibold">Dismissed</span>}
        </div>
        <div className="absolute top-2.5 right-2.5">
          <ScoreRing score={score} />
        </div>
      </div>

      <div className="p-4 flex flex-col gap-3 flex-1">
        {/* Price + discount */}
        <div className="flex items-center justify-between gap-2">
          <span className="text-[22px] font-bold tabular text-accent tracking-tight">{fmtCurrency(opportunity.price)}</span>
          {discount != null && (
            <span className="px-2 py-0.5 rounded-md bg-danger text-white text-xs font-semibold tabular">-{fmt(discount, 1)}% vs market</span>
          )}
        </div>

        {/* Title, location, stats */}
        <div>
          <h3 className="text-[15px] font-semibold text-primary leading-snug line-clamp-2">{opportunity.title}</h3>
          {opportunity.location && (
            <p className="mt-0.5 text-xs text-secondary flex items-center gap-1 truncate"><MapPin className="w-3.5 h-3.5 flex-shrink-0" /> {opportunity.location}</p>
          )}
          {stats.length > 0 && <p className="mt-1 text-[13px] text-secondary tabular truncate">{stats.join(' · ')}</p>}
        </div>

        {/* AI summary */}
        <div className="text-sm">
          <div className="flex items-center gap-2 flex-wrap text-xs mb-1">
            <span className="px-2 py-0.5 rounded-full bg-accent-soft text-accent font-medium">{aiAnalysis?.investmentType || 'N/A'}</span>
            <span className="text-muted">{aiAnalysis?.confidence || 'medium'} confidence · {getScoreBadge(score)}</span>
          </div>
          {aiAnalysis?.reasoning && <p className={cn('text-secondary text-sm leading-relaxed', !expanded && 'line-clamp-3')}>{aiAnalysis.reasoning}</p>}
        </div>

        {/* Action message */}
        {actionMessage && (
          <div className={cn('p-2.5 rounded-lg text-sm font-medium border', actionMessage.type === 'success' ? 'bg-accent-soft text-accent border-accent/30' : 'bg-danger-soft text-danger border-danger/30')}>
            {actionMessage.text}
          </div>
        )}

        {/* Expandable details */}
        {expanded && (
          <div className="space-y-3 pt-3 border-t border-line text-sm">
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-page border border-line rounded-xl p-3">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-muted">Your portfolio avg</div>
                <div className="text-primary font-semibold tabular">{comparison?.userAvgPricePerSqm != null ? `€${fmt(comparison.userAvgPricePerSqm)}/m²` : 'N/A'}</div>
                {comparison?.userDiscount != null && <div className="text-xs text-accent">{fmt(comparison.userDiscount, 1)}% below</div>}
              </div>
              <div className="bg-page border border-line rounded-xl p-3">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-muted">Market avg</div>
                <div className="text-primary font-semibold tabular">{comparison?.marketAvgPricePerSqm != null ? `€${fmt(comparison.marketAvgPricePerSqm)}/m²` : 'N/A'}</div>
                {comparison?.marketDiscount != null && <div className="text-xs text-accent">{fmt(comparison.marketDiscount, 1)}% below</div>}
              </div>
            </div>
            <p className="text-xs text-muted">Based on {marketData?.marketSampleSize || 0} similar properties in the area</p>

            {(aiAnalysis?.strengths || []).length > 0 && (
              <div>
                <h4 className="font-semibold text-primary mb-1">Strengths</h4>
                <ul className="list-disc pl-5 space-y-0.5 text-secondary">
                  {aiAnalysis.strengths.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              </div>
            )}
            {(aiAnalysis?.risks || []).length > 0 && (
              <div>
                <h4 className="font-semibold text-primary mb-1">Risks</h4>
                <ul className="list-disc pl-5 space-y-0.5 text-secondary">
                  {aiAnalysis.risks.map((r, i) => <li key={i}>{r}</li>)}
                </ul>
              </div>
            )}
            {aiAnalysis?.redFlags && aiAnalysis.redFlags.length > 0 && (
              <div>
                <h4 className="font-semibold text-danger mb-1">Red flags</h4>
                <ul className="list-disc pl-5 space-y-0.5 text-danger/90">
                  {aiAnalysis.redFlags.map((f, i) => <li key={i}>{f}</li>)}
                </ul>
              </div>
            )}
            <div className="bg-amber-soft border border-amber/30 rounded-xl p-3">
              <h4 className="font-semibold text-primary mb-1">Price negotiation</h4>
              <p className="text-secondary"><strong className="text-primary">Suggested offer:</strong> {fmtCurrency(aiAnalysis?.priceNegotiation?.suggestedOffer)}</p>
              <p className="text-secondary"><strong className="text-primary">Maximum price:</strong> {fmtCurrency(aiAnalysis?.priceNegotiation?.maxPrice)}</p>
              <p className="text-xs text-secondary mt-1">{aiAnalysis?.priceNegotiation?.reasoning || 'N/A'}</p>
            </div>
            {aiAnalysis?.recommendedAction && (
              <div>
                <h4 className="font-semibold text-primary mb-1">Recommended action</h4>
                <p className="text-secondary">{aiAnalysis.recommendedAction}</p>
              </div>
            )}
            {aiAnalysis?.estimatedROI && (
              <div>
                <h4 className="font-semibold text-primary mb-1">Estimated ROI</h4>
                <p className="text-secondary">{aiAnalysis.estimatedROI}</p>
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="mt-auto pt-1 grid grid-cols-[1fr_1fr_auto] gap-2">
          <Button variant="primary" size="sm" onClick={handleExpand}>
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            {expanded ? 'Less' : 'Report'}
          </Button>
          <Button variant="secondary" size="sm" onClick={handleAddToTracked} disabled={isProcessing || isTracked} loading={isProcessing && !isTracked}>
            {isTracked ? <Check className="w-4 h-4" /> : !isProcessing && <Plus className="w-4 h-4" />}
            {isTracked ? 'Tracked' : 'Track'}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setShowDismissConfirm(true)} disabled={isProcessing || isDismissed} aria-label="Dismiss">
            <X className="w-4 h-4" /> <span className="hidden sm:inline">Dismiss</span>
          </Button>
        </div>

        {/* Metadata */}
        <div className="flex items-center justify-between gap-2 text-xs text-muted pt-2 border-t border-line">
          <span className="truncate">Detected {new Date(opportunity.detectedAt).toLocaleDateString()} from "{opportunity.detectedFromSearchName}"</span>
          {opportunity.propertyUrl && (
            <a href={opportunity.propertyUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-accent hover:underline flex-shrink-0">
              Listing <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
        </div>
      </div>

      {/* Dismiss Confirmation */}
      <Sheet isOpen={showDismissConfirm} onClose={() => setShowDismissConfirm(false)} mode="dialog" size="sm" title="Dismiss opportunity">
        <p className="text-sm text-primary">
          Are you sure you want to dismiss <strong>"{opportunity.title}"</strong>?
        </p>
        <p className="text-xs text-secondary mt-2">This opportunity will be moved to dismissed and hidden from the active list.</p>
        <div className="flex justify-end gap-2 mt-5">
          <Button variant="secondary" onClick={() => setShowDismissConfirm(false)}>Cancel</Button>
          <Button variant="danger-solid" onClick={handleDismiss}>Dismiss</Button>
        </div>
      </Sheet>
    </article>
  );
}
